import './style/exportInvoices.scss';

import {
    AwesomeIcon,
    ChangeFunction,
    ChangeKeyFunction,
    Checkbox,
    DvrdSelect,
    Loader,
    SelectItemShape
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, useState} from 'react';
import ExportProcess from "../../../models/exportProcessModel";
import {BolmateButton, BolmateSelect, DvrdDatePicker} from "../../controls";
import {
    AccessKey,
    CustomerType,
    ExportNameAttr,
    ExportProcessStatus,
    Fulfilment,
    InvoiceExportType
} from "../../../util/interfaces";
import CountryField from "../../controls/country/field/countryField";
import classNames from 'classnames';
import useRight from "../../misc/useRight";
import InfoBanner from "../../misc/infoBanner";

interface Props {
    onChange: ChangeKeyFunction;
    onDownloadProcess: (process: ExportProcess) => MouseEventHandler;
    onSubmitProcess: MouseEventHandler;
    onChangeCountry: ChangeFunction<string>;
    onChangeNameAttr: ChangeFunction<ExportNameAttr>;
    onChangeReverseCharge: ChangeFunction;
    onChangeCustomerType: ChangeFunction;
    onClickFields: MouseEventHandler;
    onChangeFulfilment: ChangeFunction<Fulfilment>;
    exportProcess: ExportProcess;
    exportProcesses: ExportProcess[];
    loadingCount: boolean;
    loadingProcesses: boolean;
    loadingSubmit: boolean;
    invoiceCount: number;
    country: string;
    fulfilment: Fulfilment;
    nameAttr: ExportNameAttr;
    reverseChargeOnly: boolean;
    customerType: CustomerType;
    selectedFieldsCount: number;
}

const typeItems: SelectItemShape[] = [
    {label: 'CSV (Excel)', value: InvoiceExportType.CSV},
    {label: 'CSV (aangepast)', value: InvoiceExportType.CSV_CUSTOM},
    {label: 'UBL (boekhouding standaard)', value: InvoiceExportType.UBL},
    {label: 'PDF', value: InvoiceExportType.PDF},
    {label: 'Gecombineerde PDF', value: InvoiceExportType.COMBINED_PDF},
];

const nameAttrItems: SelectItemShape[] = [
    {label: 'Ordernummer (Bol)', value: ExportNameAttr.ORDER_ID},
    {label: 'Factuurnummer', value: ExportNameAttr.INVOICE_NUMBER}
];

const customerItems: SelectItemShape[] = [
    {label: 'Alle klanten', value: CustomerType.BOTH},
    {label: 'Zakelijke klanten', value: CustomerType.BUSINESS},
    {label: 'Particuliere klanten', value: CustomerType.PRIVATE},
];

const fulfilmentItems: SelectItemShape[] = [
    {label: 'Iedere verzending', value: Fulfilment.BOTH},
    {label: 'Eigen verzending', value: Fulfilment.FBR},
    {label: 'LVB', value: Fulfilment.FBB},
];

export default function ExportInvoices(props: Props) {
    const [canExport, canDownload] = useRight(
        AccessKey.EXPORT_SETTINGS_EXPORT,
        AccessKey.EXPORT_SETTINGS_DOWNLOAD
    );

    const {
        exportProcess,
        exportProcesses,
        loadingCount,
        loadingProcesses,
        onChange,
        onDownloadProcess,
        onSubmitProcess,
        invoiceCount,
        loadingSubmit,
        country,
        onChangeCountry,
        onChangeNameAttr,
        nameAttr,
        onChangeReverseCharge,
        reverseChargeOnly,
        customerType,
        onChangeCustomerType,
        selectedFieldsCount,
        onClickFields,
        onChangeFulfilment,
        fulfilment
    } = props;

    const countLabel = `${invoiceCount} ${invoiceCount === 1 ? 'factuur' : 'facturen'} geselecteerd`;
    const [filtersOpen, setFiltersOpen] = useState(canExport);
    const submitDisabled = loadingCount || !exportProcess.fromDate || !exportProcess.toDate;

    function onToggleFilters() {
        setFiltersOpen(!filtersOpen);
    }

    function onChangeDate(key: string) {
        return function (value: IDate) {
            onChange(key)(value.format('YYYY-MM-DD'));
        };
    }

    function renderCreate() {
        const nameAttrVisible = [InvoiceExportType.PDF, InvoiceExportType.UBL].includes(exportProcess.exportType);

        return (
            <div className='block create'>
                <Loader active={loadingSubmit}/>
                <div className='header-container'>
                    <h3 className='block-title'>Exporteren</h3>
                    <label className='block-sub'>Exporteren van de facturen of gegevens van een bepaalde
                        periode.</label>
                </div>

                <div className='type-container'>
                    <BolmateSelect
                        onChange={onChange('export_type')}
                        value={exportProcess.exportType}
                        items={typeItems}
                        label='Bestandstype'
                        disabled={loadingCount || !canExport}
                    />

                    {exportProcess.exportType === InvoiceExportType.COMBINED_PDF && (
                        <label className='combine-info'><AwesomeIcon name='info-circle' className='info'/> Alle factuur-
                            en BTW totalen worden bij elkaar opgeteld en op &eacute;&eacute;n factuur gezet.</label>
                    )}

                    {nameAttrVisible && (
                        <BolmateSelect
                            value={nameAttr}
                            onChange={onChangeNameAttr}
                            items={nameAttrItems}
                            label='Bestandsnaam baseren op'
                            disabled={loadingCount || !canExport}
                        />
                    )}
                </div>

                {exportProcess.exportType === InvoiceExportType.CSV_CUSTOM && renderFieldsSelection()}
                {renderFilters()}

                <div className='btn-container'>
                    <BolmateButton
                        onClick={onSubmitProcess}
                        label='Aanmaken'
                        disabled={submitDisabled || !canExport}
                    />
                    <label className='invoice-count'>{countLabel}</label>
                </div>

                <div className='disclaimer'>
                    <label>Het aanmaken van het bestand kan even duren. Zodra deze klaar is, staat de download hieronder
                        klaar. Je kunt ondertussen deze pagina verlaten.</label>
                    <label className='italic'>Gebruik van gegevens op eigen risico.</label>
                </div>
            </div>
        );
    }

    function renderFieldsSelection() {
        const fieldLabel = selectedFieldsCount === 1 ? 'veld' : 'velden';

        return (
            <div className='fields-selection-container'>
                <label className='fields-count'>
                    {selectedFieldsCount} {fieldLabel} geselecteerd
                </label>
                <BolmateButton
                    onClick={onClickFields}
                    label='Velden kiezen'
                    disabled={loadingSubmit || loadingCount || !canExport}
                />
            </div>
        );
    }

    function renderFilters() {
        return (
            <div className={classNames('filters-container', !filtersOpen && 'closed')}>
                <div
                    className='filter-header-container'
                    onClick={canExport ? onToggleFilters : undefined}
                >
                    <div className='line'/>
                    <label className='filter-header-title'>Filters</label>
                    <AwesomeIcon name='chevron-down' className='caret'/>
                </div>

                <div className='filters'>
                    <div className='row'>
                        <label className='period-label'>Periode</label>
                        <div className='period-container'>
                            <DvrdDatePicker
                                onChange={onChangeDate('from_date')}
                                value={exportProcess.fromDateMoment}
                                label='Vanaf'
                                placeholder='dd/mm/jjjj'
                                disabled={loadingCount || !canExport}
                                dateFormat='DD/MM/YYYY'
                                closeOnChange
                            />
                            <div className='center-container'>
                                <span className='sep'>-</span>
                            </div>
                            <DvrdDatePicker
                                onChange={onChangeDate('to_date')}
                                value={exportProcess.toDateMoment}
                                label='Tot en met'
                                placeholder='dd/mm/jjjj'
                                disabled={loadingCount || !canExport}
                                dateFormat='DD/MM/YYYY'
                                closeOnChange
                            />
                        </div>
                    </div>

                    <div className='row-2'>
                        <div className='filter-container'>
                            <CountryField
                                onChange={onChangeCountry}
                                value={country ?? ''}
                                label='Land'
                                disabled={!canExport}
                                withAll
                                labelAsValue
                            />
                        </div>
                        <div className='filter-container'>
                            <Checkbox
                                checked={reverseChargeOnly}
                                onCheck={onChangeReverseCharge}
                                label='Alleen verlegde BTW'
                                className='row-check'
                                disabled={!canExport}
                            />
                        </div>
                    </div>

                    <div className='row js'>
                        <DvrdSelect
                            value={customerType}
                            onChange={onChangeCustomerType}
                            label='Type klant'
                            items={customerItems}
                            disabled={!canExport}
                        />
                    </div>

                    <div className='row js'>
                        <DvrdSelect
                            value={fulfilment}
                            onChange={onChangeFulfilment}
                            label='Type verzending'
                            items={fulfilmentItems}
                            disabled={!canExport}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderProcesses() {
        return (
            <div className='block processes'>
                <Loader active={loadingProcesses}/>
                <div className='header-container'>
                    <h3 className='block-title'>Downloads</h3>
                    <label className='block-sub'>Download de aangemaakte export hieronder.</label>
                </div>

                <div className='process-list'>
                    <div className='row head'>
                        <label>AANVRAAG DATUM</label>
                        <label>PERIODE</label>
                        <label>BESTANDSTYPE</label>
                        <label>ACTIE</label>
                    </div>

                    {exportProcesses.map((process) => (
                        <div className='row' key={process.id}>
                            <label>{process.createdAtMoment.format('DD/MM/YYYY')}</label>
                            <label>{process.periodLabel}</label>
                            <label>{process.translatedExportType}</label>
                            {renderProcessAction(process)}
                        </div>
                    ))}
                    {exportProcesses.length === 0 &&
                        <label className='empty-label'>- nog geen bestanden aangemaakt -</label>}
                </div>
            </div>
        );
    }

    function renderProcessAction(process: ExportProcess) {
        if (process.status === ExportProcessStatus.EXPORTING) {
            return (
                <label className='exporting'>
                    <Loader className='status-loader' size='1rem' thickness='.2rem' active disableBackground/>
                    Bezig met exporteren...
                </label>
            );
        }

        if (process.status === ExportProcessStatus.ERROR) {
            const error = process.getFlex('error');
            return <label className='error'>Fout{error ? ` - ${error}` : ''}</label>;
        }

        return (
            <label
                className={classNames('download', !canDownload && 'cursor-default')}
                onClick={canDownload ? onDownloadProcess(process) : undefined}
            >
                {canDownload ? 'Downloaden' : 'Gereed'}
            </label>
        );
    }

    return (
        <div className='export-invoices'>
            <InfoBanner
                featureKey='export_invoices'
                variant='info'
                title='Facturen exporteren'
                message='Exporteer jouw facturen en factuurgegevens.'
                showForNewUsersOnly={true}
            />
            {renderCreate()}
            {renderProcesses()}
        </div>
    );
}
